/**
 * Registers this browser as RunDiffusion's first-party web client.
 *
 * The API decides which library response shape to serve from WHO is calling,
 * not from a query parameter. A browser holding a `web_app` Connected Device is
 * served the `node_run` shape the gallery renders; everyone else — the
 * Photoshop/Miro/Revit plugins and public API callers — gets the flat `result`
 * shape. The kind lives on the device record the server wrote, so it cannot be
 * asserted by sending a header.
 *
 * The id is stored per browser, exactly as a plugin stores its own. Clearing
 * site data therefore produces a new Connected Device row rather than
 * resurrecting the old one, which is the honest outcome: it IS a new client, and
 * the old row stays revocable from the Connected Devices page.
 *
 * Registration is best-effort. A failure must not block the app: the caller
 * still renders, it just has no device id to send, and the shape it gets back
 * will not be the one it wants. Callers treat a null return as "not registered
 * yet" and retry on the next load.
 */

import {
	getWebClientDeviceId,
	removeWebClientDeviceId,
	setWebClientDeviceId,
} from './localStorage';

/**
 * The device id for this browser, or null if it has not registered yet.
 *
 * Storage lives in `localStorage.ts` with every other persisted key, which also
 * supplies the incognito/hardened-settings guarding: those accessors already
 * swallow the throw that private browsing raises on access.
 */
export function readWebClientDeviceId(): string | null {
	return getWebClientDeviceId();
}

/** Forget this browser's registration, e.g. after it was revoked elsewhere. */
export function clearWebClientDeviceId(): void {
	removeWebClientDeviceId();
}

/**
 * Error codes meaning the stored device id can no longer identify this browser
 * as a live `web_app` client.
 *
 * A device can be revoked from anywhere: this page, another browser, a
 * sign-out-everywhere. The revoke check runs before anything else in auth, so a
 * dead id does not merely lose us the node_run shape — it fails EVERY request
 * with 401 before the caller is even resolved.
 *
 * `PLUGIN_NOT_ALLOWED` belongs here even though nothing about it says "device".
 * It is the code an Enterprise browser actually receives for the deleted or
 * legacy id case: a device record with no `plugin_kind` is classified as a
 * direct-API client inside authentication, and a team without
 * `pluginAccessSettings.publicApiAccess` is refused there with a 403 — before
 * any view runs, so `WEB_APP_DEVICE_REQUIRED` never gets the chance to fire.
 * A genuine `web_app` device is exempt from that gate entirely, so this code
 * can only ever mean the stored id is the wrong kind: discarding it is the
 * recovery, and the replacement registers as `web_app` and is exempt in turn.
 */
const REJECTED_WEB_CLIENT_DEVICE_CODES = new Set([
	'TOKEN_REVOKED',
	'WEB_APP_DEVICE_REQUIRED',
	'PLUGIN_NOT_ALLOWED',
]);

/**
 * Drop the stored id when a response says it is unusable as this browser's
 * web client identity, so the next call registers a fresh one.
 *
 * Without this the browser is wedged: `ensureWebClientDeviceId` short-circuits
 * on the stored id and never re-checks it, so every request keeps presenting the
 * same rejected device and reloading changes nothing. Recovery would mean
 * clearing site data by hand.
 *
 * Returns true when something was forgotten, so a caller can retry.
 */
export function forgetWebClientDeviceIfRejected(code?: string | null): boolean {
	if (!code || !REJECTED_WEB_CLIENT_DEVICE_CODES.has(code)) return false;
	clearWebClientDeviceId();
	return true;
}

export interface RegisterWebClientInput {
	baseUrl: string;
	idToken: string;
}

/**
 * Return this browser's device id, registering one if it has none.
 *
 * Idempotent from the caller's point of view: a stored id short-circuits, so
 * calling this on every load costs nothing after the first. The endpoint itself
 * always mints a new record, which is why the stored id must be checked first.
 */
export async function ensureWebClientDeviceId(
	input: RegisterWebClientInput,
): Promise<string | null> {
	const existing = readWebClientDeviceId();
	if (existing) return existing;

	const url = `${input.baseUrl.replace(/\/$/, '')}/auth/devices/web`;
	try {
		const resp = await fetch(url, {
			method: 'POST',
			// Registration binds the browser to the USER, not to an account, so
			// the request carries no account selection.
			headers: {
				'Authorization': `Bearer ${input.idToken}`,
			},
		});
		if (!resp.ok) return null;

		const body: any = await resp.json();
		const deviceId: unknown = body && body.device_id;
		if (typeof deviceId !== 'string' || !deviceId) return null;

		setWebClientDeviceId(deviceId);
		return deviceId;
	} catch {
		// Network failure, offline, blocked request. Unregistered is a valid
		// state; the next load tries again.
		return null;
	}
}
